import type { TFunction } from 'i18next';
import { z } from 'zod';
import {
  AccommodationUnitPattern,
  PlaceholderCostType,
  PlaceholderStatus,
  PlaceholderType,
  TransportKind,
  type GetPlaceholderResponse,
} from '@/api/generated/schemas';

/** UI ref subkategorie (drží bohatý objekt kvůli chipu). */
const subcategoryRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullable(),
});

/** UI ref destinace. */
const destinationRefSchema = z.object({
  destinationId: z.number(),
  name: z.string(),
  code: z.string().nullish(),
});

/** UI ref podtypu ubytovacího zařízení. */
const facilitySubTypeRefSchema = z.object({
  id: z.number(),
  name: z.string(),
});

/** UI ref kategorie ubytování (položka číselníku). */
const codebookRefSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string().nullable(),
});

/** UI ref položky vybavení. */
const equipmentRefSchema = z.object({
  id: z.number(),
  name: z.string(),
});

/**
 * Factory Zod schématu formuláře placeholderu. Lokalizované hlášky se vkládají přímo do schématu
 * (single source of truth), proto se staví z `t` až za běhu. Podmíněné povinnosti (subkategorie
 * vždy, druh dopravy jen pro Transport, podtyp + kategorie ubytování jen pro Accommodation) se řeší
 * object-level `superRefine`, aby hodnoty zůstaly `ref | null`.
 */
export function buildPlaceholderFormSchema(t: TFunction) {
  return z
    .object({
      type: z.enum([
        PlaceholderType.Accommodation,
        PlaceholderType.Transport,
        PlaceholderType.Activities,
        PlaceholderType.LocalGuides,
        PlaceholderType.OtherServices,
      ]),
      costType: z.enum([
        PlaceholderCostType.Calculation,
        PlaceholderCostType.Onsite,
        PlaceholderCostType.None,
      ]),
      name: z.string().trim().min(1, t('schemes.placeholderForm.validation.nameRequired')),
      subcategory: subcategoryRefSchema.nullable(),
      destination: destinationRefSchema.nullable(),
      showInProgram: z.boolean(),
      code: z.string(),
      note: z.string(),
      status: z.enum([PlaceholderStatus.Active, PlaceholderStatus.Archived]),
      // Ubytování
      facilitySubType: facilitySubTypeRefSchema.nullable(),
      accommodationCategory: codebookRefSchema.nullable(),
      unitPatterns: z.array(
        z.enum([
          AccommodationUnitPattern.Single,
          AccommodationUnitPattern.Double,
          AccommodationUnitPattern.Triple,
          AccommodationUnitPattern.Quad,
        ])
      ),
      equipment: z.array(equipmentRefSchema),
      // Doprava
      transportKind: z
        .enum([TransportKind.Bus, TransportKind.Boat, TransportKind.Train])
        .nullable(),
    })
    .superRefine((values, ctx) => {
      // Subkategorie, typ zařízení i kategorie ubytování jsou nepovinné; povinný zůstává jen
      // druh dopravy u typu Doprava.
      if (values.type === PlaceholderType.Transport && values.transportKind == null) {
        ctx.addIssue({
          code: 'custom',
          path: ['transportKind'],
          message: t('schemes.placeholderForm.validation.transportKindRequired'),
        });
      }
    });
}

export type PlaceholderFormValues = z.infer<ReturnType<typeof buildPlaceholderFormSchema>>;

/** Prázdné výchozí hodnoty pro režim zakládání. */
export function emptyPlaceholderFormValues(): PlaceholderFormValues {
  return {
    type: PlaceholderType.Accommodation,
    costType: PlaceholderCostType.Calculation,
    name: '',
    subcategory: null,
    destination: null,
    showInProgram: true,
    code: '',
    note: '',
    status: PlaceholderStatus.Active,
    facilitySubType: null,
    accommodationCategory: null,
    unitPatterns: [],
    equipment: [],
    transportKind: null,
  };
}

/** Mapuje detail placeholderu z API na hodnoty formuláře (režim editace), hydratuje selector refy. */
export function fromGetPlaceholderResponse(
  response: GetPlaceholderResponse
): PlaceholderFormValues {
  return {
    type: response.type,
    costType: response.costType,
    name: response.name ?? '',
    subcategory:
      response.subcategoryId != null
        ? {
            id: response.subcategoryId,
            name: response.subcategoryName ?? '',
            code: null,
          }
        : null,
    destination:
      response.destinationId != null
        ? {
            destinationId: response.destinationId,
            name: response.destinationName ?? '',
            code: null,
          }
        : null,
    showInProgram: response.showInProgram,
    code: response.code ?? '',
    note: response.note ?? '',
    status: response.status,
    facilitySubType:
      response.facilitySubTypeId != null
        ? { id: response.facilitySubTypeId, name: response.facilitySubTypeName ?? '' }
        : null,
    accommodationCategory:
      response.accommodationCategoryCbId != null
        ? {
            id: response.accommodationCategoryCbId,
            name: response.accommodationCategoryName ?? '',
            code: null,
          }
        : null,
    unitPatterns: response.unitPatterns,
    equipment: response.equipment.map((item) => ({ id: item.id, name: item.name ?? '' })),
    transportKind: response.transportKind ?? null,
  };
}
